import { t } from "../../i18n";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../../api";
import { SettingsPage } from "../settings/ProfilePage";
import {
  CustomModelEditor,
  type CustomModelOption,
  type CustomModelRecord,
  type CustomModelScope,
} from "../../components/models/CustomModelEditor";

const scopes: CustomModelScope[] = ["platform", "organization", "personal"];

export function CustomModelsPage({
  availableScopes,
}: {
  availableScopes: CustomModelScope[];
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const requestedScope =
    (query.get("scope") as CustomModelScope | null) ??
    availableScopes[0] ??
    "personal";
  const organizationId = query.get("organizationId") ?? undefined;
  const editingId = query.get("edit");
  const creating = query.get("new") === "1";
  const [models, setModels] = useState<CustomModelRecord[]>([]);
  const [options, setOptions] = useState<
    Record<CustomModelScope, CustomModelOption[]>
  >({ platform: [], organization: [], personal: [] });
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [rows, ...available] = await Promise.all([
        api<CustomModelRecord[]>(
          `/custom-models${organizationId ? `?scope=organization&organizationId=${organizationId}` : ""}`,
        ),
        ...availableScopes.map(
          async (scope) =>
            [
              scope,
              await api<CustomModelOption[]>(
                `/custom-models/available-models?scope=${scope}${scope === "organization" && organizationId ? `&organizationId=${organizationId}` : ""}`,
              ),
            ] as const,
        ),
      ]);
      setModels(rows);
      setOptions((current) => ({
        ...current,
        ...Object.fromEntries(available),
      }));
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Custom models could not be loaded",
      );
    }
  }, [availableScopes, organizationId]);
  useEffect(() => {
    void load();
  }, [load]);

  const selected = models.find((model) => model.id === editingId) ?? null;
  const activeScope = selected?.scope ?? requestedScope;
  const closeEditor = () =>
    navigate(
      location.pathname +
        (organizationId
          ? `?scope=organization&organizationId=${organizationId}`
          : ""),
    );
  const save = (model: CustomModelRecord) => {
    setModels((current) =>
      current.some((item) => item.id === model.id)
        ? current.map((item) => (item.id === model.id ? model : item))
        : [...current, model],
    );
    closeEditor();
  };
  const remove = async (model: CustomModelRecord) => {
    if (!window.confirm(`Delete ${model.name}?`)) return;
    await api(`/custom-models/${model.id}`, { method: "DELETE" });
    setModels((current) => current.filter((item) => item.id !== model.id));
  };

  if (creating || selected) {
    return (
      <SettingsPage
        title={t("Custom models")}
        description="Create a routing or fallback model from the models available at its resource scope."
      >
        <CustomModelEditor
          value={selected}
          scope={activeScope}
          organizationId={organizationId}
          options={options[activeScope]}
          availableScopes={availableScopes}
          onScopeChange={(scope) =>
            navigate(
              `${location.pathname}?${editingId ? `edit=${editingId}` : "new=1"}&scope=${scope}${scope === "organization" && organizationId ? `&organizationId=${organizationId}` : ""}`,
            )
          }
          onSaved={save}
          onCancel={closeEditor}
        />
      </SettingsPage>
    );
  }

  return (
    <SettingsPage
      title={t("Custom models")}
      description="Custom models behave like normal models in chat and settings."
    >
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <Link
          className="button"
          to={
            organizationId
              ? `/settings/models?organizationId=${organizationId}`
              : "/settings/models"
          }
        >
          {t("Back to models")}
        </Link>
        <button
          type="button"
          className="button-primary"
          onClick={() =>
            navigate(
              `${location.pathname}?new=1&scope=${requestedScope}${organizationId ? `&organizationId=${organizationId}` : ""}`,
            )
          }
        >
          {t("New custom model")}
        </button>
      </div>
      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
      <div className="space-y-3">
        {models.map((model) => (
          <article
            key={model.id}
            className="panel flex flex-wrap items-center justify-between gap-3 p-4"
          >
            <div>
              <h2 className="font-semibold">{model.name}</h2>
              <p className="text-sm text-stone-500">
                {model.type === "routing" ? "Custom routing" : "Fallback"} ·{" "}
                {model.scope}
              </p>
              {model.description && (
                <p className="mt-1 text-sm text-stone-500">
                  {model.description}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="button"
                onClick={() =>
                  navigate(
                    `${location.pathname}?edit=${model.id}&scope=${model.scope}${model.scope === "organization" && organizationId ? `&organizationId=${organizationId}` : ""}`,
                  )
                }
              >
                {t("Edit")}
              </button>
              <button
                type="button"
                className="button"
                onClick={() => void remove(model)}
              >
                {t("Delete")}
              </button>
            </div>
          </article>
        ))}
        {!models.length && (
          <div className="panel p-8 text-center text-sm text-stone-500">
            {t("No custom models yet.")}
          </div>
        )}
      </div>
    </SettingsPage>
  );
}
